import React, { useEffect, useRef } from 'react';
import './ConfirmModal.css';

type ConfirmModalProps = {
  isOpen: boolean;
  title: string;
  /** Plain string or rich content (e.g. bold filename). */
  message: string | React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  isConfirmLoading?: boolean;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
  /** Destructive confirmations: red confirm button, subdued cancel, extra spacing. */
  variant?: 'default' | 'danger';
};

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  isConfirmLoading = false,
  onConfirm,
  onCancel,
  variant = 'default',
}) => {
  // Block body scroll when modal is open
  // Используем ref для сохранения позиции скролла
  const savedScrollYRef = useRef<number>(0);
  const modalRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    if (isOpen) {
      // Save current scroll position БЕЗ изменения скролла
      savedScrollYRef.current = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
      
      // Блокируем скролл без изменения позиции - используем overflow: hidden
      // без position: fixed, чтобы избежать визуального "прыжка"
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
      
      // Предотвращаем автоскролл при фокусе на модалке
      if (modalRef.current) {
        modalRef.current.focus();
      }
    } else {
      // Restore scroll - убираем стили
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      
      // Восстанавливаем позицию скролла только если она была сохранена
      // Используем requestAnimationFrame для плавного восстановления
      if (savedScrollYRef.current > 0) {
        requestAnimationFrame(() => {
          window.scrollTo({
            top: savedScrollYRef.current,
            behavior: 'auto'
          });
        });
      }
    }
    return () => {
      // Cleanup on unmount - восстанавливаем стили
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  return (
    <div
      ref={modalRef}
      className="confirm-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      tabIndex={-1}
      style={{ outline: 'none' }}
    >
      <div className={`confirm-modal${variant === 'danger' ? ' confirm-modal--danger' : ''}`}>
        <div className="confirm-modal-header">
          <h3 className="confirm-modal-title">{title}</h3>
          <button className="confirm-modal-close" onClick={onCancel} aria-label="Close">
            ×
          </button>
        </div>
        <div className={`confirm-modal-body${variant === 'danger' ? ' confirm-modal-body--danger' : ''}`}>
          {typeof message === 'string' ? (
            <p className="confirm-modal-message">{message}</p>
          ) : (
            <div className="confirm-modal-message-rich">{message}</div>
          )}
        </div>
        <div className={`confirm-modal-footer${variant === 'danger' ? ' confirm-modal-footer--danger' : ''}`}>
          <button className="confirm-modal-btn confirm-modal-btn-secondary" onClick={onCancel} disabled={isConfirmLoading}>
            {cancelText}
          </button>
          <button
            className={
              variant === 'danger'
                ? 'confirm-modal-btn confirm-modal-btn-danger'
                : 'confirm-modal-btn confirm-modal-btn-primary'
            }
            onClick={onConfirm}
            disabled={isConfirmLoading}
          >
            {isConfirmLoading ? 'Working…' : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;

